/**
 * DirectMessagesWidget — small dashboard/profile card that surfaces the
 * user's latest DM conversations with a jump-to-inbox CTA. Keeps the
 * inbox one click away without cluttering the main navigation.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { MessageCircle, ArrowUpRight, Send } from 'lucide-react';

const rel = (iso) => {
  if (!iso) return '';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ''; }
};

const DirectMessagesWidget = ({ compact = false }) => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/dm/conversations`);
        if (!alive) return;
        setConversations(r.data.conversations || []);
        setUnread(r.data.unread_count || 0);
      } catch { /* silent */ }
      finally { if (alive) setLoading(false); }
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, [API, user]);

  const preview = conversations.slice(0, compact ? 2 : 3);

  return (
    <div className="pro-card p-5 bg-card border border-border rounded-2xl" data-testid="dm-widget">
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-full bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
          <MessageCircle className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <span className="eyebrow">Direct messages</span>
          <div className="font-heading text-base font-bold text-foreground leading-tight">
            {unread > 0 ? `${unread} unread` : 'Inbox'}
          </div>
        </div>
        {unread > 0 && (
          <span
            className="min-w-[20px] h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center px-1.5 flex-shrink-0"
            data-testid="dm-widget-unread"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </div>

      <div className="mt-3 space-y-1.5 min-h-[80px]" data-testid="dm-widget-list">
        {loading && <div className="text-xs text-muted-foreground">Loading…</div>}
        {!loading && preview.length === 0 && (
          <div className="text-xs text-muted-foreground">
            No messages yet. Open a member's profile in the forum to say hi.
          </div>
        )}
        {preview.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => navigate(`/messages/${c.id}`)}
            data-testid={`dm-widget-conv-${c.id}`}
            className={`w-full text-left flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors ${
              c.unread ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-muted'
            }`}
          >
            <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold text-foreground flex-shrink-0">
              {(c.peer?.name || '?').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-xs text-foreground truncate flex-1">{c.peer?.name || 'Unknown'}</span>
                {c.unread && (
                  <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" aria-label="Unread" />
                )}
                <span className="text-[9px] text-muted-foreground flex-shrink-0">{rel(c.last_message_at)}</span>
              </div>
              <div className="text-[11px] text-muted-foreground truncate">{c.last_snippet || 'Say hello.'}</div>
            </div>
          </button>
        ))}
      </div>

      <Button
        onClick={() => navigate('/messages')}
        size="sm"
        variant="outline"
        className="w-full mt-3 rounded-full font-semibold"
        data-testid="dm-widget-open-inbox"
      >
        <Send className="w-3 h-3 mr-1" /> Open inbox <ArrowUpRight className="ml-1 w-3 h-3" />
      </Button>
    </div>
  );
};

export default DirectMessagesWidget;
